import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import CourseTheatreReicpe from "../../flows/CourseTheatreRecipe";
import { useMutation } from 'react-apollo';
import gql from 'graphql-tag';
import { useTranslation } from 'react-i18next';
import setPropertyVh from '../../utils/setPropertyVh';
import UserContext from "pages/Login/UserContext";
import { updateUserProgress } from '../../graphql/mutations';
import TopNavbar from 'components/TopNavbar/TopNavbar';
import StepRequirement from "components/StepRequirement/StepRequirement";
import LessonPanel from "./LessonPanel";
import LessonLobby from "./LessonLobby";
import LessonStepper from "./LessonStepper";
import SomethingWrong from 'components/SomethingWrong/SomethingWrong';
import RegisterModal from 'components/RegisterModal/RegisterModal';
import CheatsListener from "utils/hooks/CheatsListener";
import { GlobalContext } from "context/GlobalContext";
import getLessonRequirementsArr from "utils/getLessonRequirementsArr";
import { isUserSubscribed } from '../../utils/subscriptionUtils';
import { useHistory } from "react-router-dom";
import StepUI from './stepUI';
import CourseProgressGrid from 'components/CourseProgress/CourseProgressGrid';
import useLessonEngine from 'hooks/useLessonEngine';
import { isNodeCompleted } from '../../utils/buildRecipeIndex';
import { migrateProgress } from '../../utils/migrateProgress';
import { track } from 'utils/mixpanel';

import './Lesson.scss';

setPropertyVh();

function Lesson({ user, match }) {
    const { params: { lessonName } } = match;
    const { handleUserUpdate } = useContext(UserContext);
    const history = useHistory();
    const { t } = useTranslation();
    const { VolumeSwitchState, setVolumeSwitchState, setIsPWallOpen } = useContext(GlobalContext);

    // -------------------------------------------------------------------------
    // Save to DB
    // -------------------------------------------------------------------------
    const [updateUser] = useMutation(gql(updateUserProgress), {
        update(cache, { data: { setUserProgress } }) {
            if (!setUserProgress) return;
            handleUserUpdate(setUserProgress);
        },
    });
    const saveToDb = useCallback(
        (payload) => updateUser({ variables: { userProgress: payload } }).then(() => {}),
        [updateUser]
    );

    // -------------------------------------------------------------------------
    // Engine
    // -------------------------------------------------------------------------
    const engine = useLessonEngine(CourseTheatreReicpe, user.userProgress, saveToDb);

    // -------------------------------------------------------------------------
    // Recipe data (derived from URL, stable)
    // -------------------------------------------------------------------------
    const currentLessonRecipe = useMemo(
        () => CourseTheatreReicpe.lessons.find((l) => l.pathname === lessonName),
        [lessonName]
    );
    const stepsRecipe = currentLessonRecipe.steps;
    const isLocked = !!currentLessonRecipe.lockedIfNotSubscribed && !isUserSubscribed(user);
    const CurrentLessonIndex = useMemo(
        () => CourseTheatreReicpe.lessons.findIndex((l) => l.pathname === lessonName),
        [lessonName]
    );

    // -------------------------------------------------------------------------
    // Derived navigation state (for LessonPanel)
    // -------------------------------------------------------------------------
    const lessonId = currentLessonRecipe.id;
    const lessonStepIds = engine.idx.childrenOf[lessonId] || [];

    // These are computed from the URL's lesson (not from activeStepItemId's lesson)
    // to avoid showing wrong state when activeStepItemId is in a different lesson.
    const isCurrentLessonCompleted = isNodeCompleted(engine.idx, lessonId, engine.progressById);
    const currentLessonHasStarted = lessonStepIds.some(stepId => {
        const siIds = engine.idx.childrenOf[stepId] || [];
        return siIds.some(siId => engine.progressById[siId]?.completed && !engine.idx.isNotMandatory[siId]);
    });

    // True when the engine's active cursor lives inside this lesson.
    // Used to distinguish "fresh user on lesson 1" (cursor here, enable normally) from
    // "user on lesson 1 visiting lesson 3 intro" (cursor elsewhere, disable non-intro steps).
    const activeStepIdCursor = engine.idx.parentOf[engine.activeStepItemId];
    const activeLessonIdCursor = engine.idx.parentOf[activeStepIdCursor];
    const isCursorInCurrentLesson = activeLessonIdCursor === lessonId;

    // LessonProgIndex: if lesson is completed, report "past" it so calcStepState
    // returns 'completed' for all steps.
    const LessonProgIndex = isCurrentLessonCompleted
        ? CurrentLessonIndex + 1
        : CurrentLessonIndex;

    // CHEATS: build a nested display structure from the flat progressById for CourseProgressGrid.
    const cheatsDisplayCourses = useMemo(() => {
        return engine.idx.courseIds.map(courseId => ({
            completed: isNodeCompleted(engine.idx, courseId, engine.progressById),
            lessons: (engine.idx.childrenOf[courseId] || []).map(lessonId => ({
                completed: isNodeCompleted(engine.idx, lessonId, engine.progressById),
                steps: (engine.idx.childrenOf[lessonId] || []).map(stepId => ({
                    completed: isNodeCompleted(engine.idx, stepId, engine.progressById),
                    stepItems: (engine.idx.childrenOf[stepId] || []).map(stepItemId => ({
                        completed: !!engine.progressById[stepItemId]?.completed,
                    })),
                })),
            })),
        }));
    }, [engine.idx, engine.progressById]); // eslint-disable-line react-hooks/exhaustive-deps

    // StepProgIndex: index of the first non-completed step in the current lesson.
    const StepProgIndex = useMemo(() => {
        const i = lessonStepIds.findIndex((id) => engine.getStepState(id) !== 'completed');
        return i === -1 ? lessonStepIds.length : i;
    }, [engine.progressById, lessonStepIds]); // eslint-disable-line react-hooks/exhaustive-deps

    // Viewing step: what step the user is looking at in LessonPanel.
    const viewingStepNode = engine.viewingStepNode;
    const CurrentSelectedStep = useMemo(() => {
        if (!viewingStepNode) return Math.min(StepProgIndex, stepsRecipe.length - 1);
        const i = stepsRecipe.findIndex((s) => s.id === viewingStepNode.id);
        if (i === -1) return 0;
        // If no explicit browse (cursor-derived) and the cursor sits on a completed
        // notMandatory-only step (intro), auto-advance the selection to the first
        // non-completed step so the user lands on the step they're about to do.
        if (!engine.viewingStepExplicit) {
            const stepItemIds = engine.idx.childrenOf[viewingStepNode.id] || [];
            const allNotMandatory = stepItemIds.length > 0 &&
                stepItemIds.every(id => engine.idx.isNotMandatory[id]);
            const stepCompleted = isNodeCompleted(engine.idx, viewingStepNode.id, engine.progressById);
            if (allNotMandatory && stepCompleted) {
                return Math.min(StepProgIndex, stepsRecipe.length - 1);
            }
        }
        return i;
    }, [viewingStepNode, stepsRecipe, StepProgIndex, engine.viewingStepExplicit, engine.idx, engine.progressById]); // eslint-disable-line react-hooks/exhaustive-deps

    const viewingStepRecipe = stepsRecipe[CurrentSelectedStep] ?? stepsRecipe[0];
    const stepName = t(viewingStepRecipe?.name ?? '');
    const stepDescription = t(viewingStepRecipe?.description ?? '');
    const stepRequirements = viewingStepRecipe?.requirements ?? null;

    // -------------------------------------------------------------------------
    // Derived navigation state (for LessonStepper)
    // -------------------------------------------------------------------------
    const activeStepId = engine.idx.parentOf[engine.activeStepItemId];
    const activeStepItemIds = engine.idx.childrenOf[activeStepId] || [];
    const CurrentSelectedStepItem = Math.max(0, activeStepItemIds.indexOf(engine.activeStepItemId));

    const activeStepIndexInLesson = lessonStepIds.indexOf(activeStepId);
    const activeStepRecipe =
        activeStepIndexInLesson >= 0
            ? stepsRecipe[activeStepIndexInLesson]
            : stepsRecipe[0];

    // -------------------------------------------------------------------------
    // Page state
    // -------------------------------------------------------------------------
    const isSingleStepLesson = currentLessonRecipe.type === 'SingleStepLesson';
    const [PageState, setPageState] = useState(() => {
        if (isSingleStepLesson) return 'LessonStepper';
        return currentLessonHasStarted ? 'LessonPanel' : 'LessonLobby';
    });

    // When replaying a completed SingleStepLesson, reset cursor to the first
    // step item (e.g. the video) so the replay starts from the beginning.
    const didReplayResetRef = useRef(false);
    useEffect(() => {
        if (!isSingleStepLesson || !isCurrentLessonCompleted || didReplayResetRef.current) return;
        didReplayResetRef.current = true;
        // Navigate to the first step item that should be shown on replay (no hideOnReplay flag).
        const allStepItems = stepsRecipe[0]?.stepItems || [];
        const firstReplayItem = allStepItems.find(item => !item.hideOnReplay);
        if (firstReplayItem) {
            engine.onNavigateToStepItem(firstReplayItem.id);
        } else {
            const firstStepId = lessonStepIds[0];
            if (firstStepId) engine.onNavigateToStep(firstStepId);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [lobbyOpenedFromPanel, setLobbyOpenedFromPanel] = useState(false);

    // -------------------------------------------------------------------------
    // CHEATS
    // -------------------------------------------------------------------------
    const [CHEATS, setCHEATS] = useState(false);
    CheatsListener(() => setCHEATS(true));

    // -------------------------------------------------------------------------
    // Audio
    // -------------------------------------------------------------------------
    const teamworkMusicRef = useRef();
    const happyMusicRef = useRef();
    const stepCompleteSfxRef = useRef();
    const lessonCompleteSfxRef = useRef();

    const playRefAudio = useCallback((ref, loop) => {
        if (!ref?.current) return;
        try {
            ref.current.play();
            if (loop) ref.current.loop = true;
        } catch (e) { console.warn('Error playing audio:', e); }
    }, []);

    const pauseRefAudio = useCallback((ref) => {
        if (!ref?.current) return;
        try {
            ref.current.pause();
            ref.current.currentTime = 0;
        } catch (e) { console.warn('Error pausing audio:', e); }
    }, []);

    const handleVolume = useCallback(() => {
        document.querySelectorAll('video, audio').forEach((el) => {
            el.volume = VolumeSwitchState ? 1 : 0;
        });
    }, [VolumeSwitchState]);

    useEffect(() => { handleVolume(); }, [VolumeSwitchState, handleVolume]);

    useEffect(() => {
        document.body.style.backgroundColor = '#f0f8ff';
        return () => { document.body.style.backgroundColor = null; };
    }, []);

    // -------------------------------------------------------------------------
    // Celebrations (driven by engine.activeCelebration)
    // -------------------------------------------------------------------------
    const [IsStepCelebrationStepCompleteClass, setIsStepCelebrationStepCompleteClass] = useState('');
    const [celebratingStepIndex, setCelebratingStepIndex] = useState(0);
    const celebrationTimeoutRef = useRef(null);
    const celebrationTimeout2Ref = useRef(null);

    useEffect(() => {
        return () => {
            if (celebrationTimeoutRef.current) clearTimeout(celebrationTimeoutRef.current);
            if (celebrationTimeout2Ref.current) clearTimeout(celebrationTimeout2Ref.current);
        };
    }, []);

    useEffect(() => {
        if (!engine.activeCelebration) return;

        // Don't celebrate during the onboarding lobby — intro was silently marked complete
        if (PageState === 'LessonLobby') {
            engine.onCelebrationDone();
            return;
        }

        pauseRefAudio(teamworkMusicRef);
        pauseRefAudio(happyMusicRef);
        setIsStepCelebrationStepCompleteClass('');

        if (engine.activeCelebration === 'step') {
            const completedStepId = engine.idx.parentOf[engine.activeStepItemId];
            const idx = lessonStepIds.indexOf(completedStepId);
            setCelebratingStepIndex(idx >= 0 ? idx : 0);

            const stepNumber = idx >= 0 ? idx + 1 : 1;
            const courseId = engine.idx.parentOf[lessonId];
            const courseNumber = engine.idx.courseIds.indexOf(courseId) + 1;
            const lessonNumber = CurrentLessonIndex + 1;
            try { track('appStepCompleted', { stepNumber, lessonNumber, courseNumber }); } catch (_) {}

            setPageState('celebrateStep');
            celebrationTimeoutRef.current = setTimeout(() => {
                setIsStepCelebrationStepCompleteClass(
                    'celebration-container--stepItem__completed bounceToLarge'
                );
                playRefAudio(stepCompleteSfxRef);
                celebrationTimeout2Ref.current = setTimeout(() => {
                    engine.onCelebrationDone();
                    if (isSingleStepLesson) {
                        history.push('/');
                    } else {
                        setPageState('LessonPanel');
                    }
                }, 2000);
            }, 1400);
        }

        if (engine.activeCelebration === 'lesson') {
            const courseId = engine.idx.parentOf[lessonId];
            const courseNumber = engine.idx.courseIds.indexOf(courseId) + 1;
            const lessonNumber = CurrentLessonIndex + 1;
            try { track('appLessonCompleted', { lessonNumber, courseNumber }); } catch (_) {}

            playRefAudio(happyMusicRef, true);
            setPageState('celebrateLesson');
            celebrationTimeoutRef.current = setTimeout(() => {
                setIsStepCelebrationStepCompleteClass(
                    'celebration-container--stepItem__completed bounceToLarge'
                );
                playRefAudio(lessonCompleteSfxRef);
            }, 1400);
        }

        if (engine.activeCelebration === 'course') {
            // Course completion — dismiss immediately, engine.isCourseCompleted will be true
            const courseId = engine.idx.parentOf[lessonId];
            const courseNumber = engine.idx.courseIds.indexOf(courseId) + 1;
            try { track('appCourseCompleted', { courseNumber }); } catch (_) {}
            engine.onCelebrationDone();
        }
    }, [engine.activeCelebration]); // eslint-disable-line react-hooks/exhaustive-deps

    // -------------------------------------------------------------------------
    // Navigation handlers
    // -------------------------------------------------------------------------
    const handleCloseLobby = () => setPageState('LessonPanel');
    const handleOpenLobby = () => {
        setLobbyOpenedFromPanel(true);
        setPageState('LessonLobby');
    };

    const handleOpenStepper = () => {
        const currentStep = stepsRecipe[CurrentSelectedStep];
        if (currentStep?.showRegisterPopup && user?.isGuest) {
            setShowRegisterModal(true);
            return;
        }
        // If the step the user is opening differs from where activeStepItemId is,
        // move the cursor there so LessonStepper shows the right content.
        // This covers: replaying a completed step, AND starting a new lesson
        // whose first step is in a different lesson than the saved activeStepItemId.
        const targetStepId = currentStep?.id;
        const activeStepId = engine.idx.parentOf[engine.activeStepItemId];
        if (targetStepId && targetStepId !== activeStepId) {
            engine.onNavigateToStep(targetStepId);
        }
        setPageState('LessonStepper');
    };

    const handleCloseStepper = () => {
        if (isSingleStepLesson) {
            history.push('/');
        } else {
            setPageState('LessonPanel');
        }
    };

    const tapOnLessonEnded = () => {
        engine.onCelebrationDone();
        setPageState('LessonPanel');
        history.push('/');
    };

    const handleRegisterModalSuccess = () => {
        setShowRegisterModal(false);
        setPageState('LessonStepper');
    };
    const handleRegisterModalClose = () => setShowRegisterModal(false);

    // -------------------------------------------------------------------------
    // addEvent bridge (child components still call addEvent)
    // -------------------------------------------------------------------------
    const addEvent = useCallback((type, element, _componentName, _index, _elementId, _date) => {
        pauseRefAudio(teamworkMusicRef);
        pauseRefAudio(happyMusicRef);
        if (type === 'continue' || type === 'completed') {
            if (element === 'step') {
                // Step-level completed events from old system — already handled by engine
                // when the last stepItem was continued. Safe to ignore here.
                return;
            }
            // If replaying a completed step and we're at its last item,
            // close the stepper — don't advance past the step boundary.
            const currentId = engine.activeStepItemId;
            const stepId = engine.idx.parentOf[currentId];
            const stepChildren = engine.idx.childrenOf[stepId] || [];
            const isLastInStep = stepChildren[stepChildren.length - 1] === currentId;
            if (engine.isActiveItemCompleted && isLastInStep) {
                handleCloseStepper();
                return;
            }
            engine.onContinue();
        } else if (type === 'back') {
            engine.onBack();
        }
    }, [engine.onContinue, engine.onBack, pauseRefAudio]); // eslint-disable-line react-hooks/exhaustive-deps

    // Page background color
    document.documentElement.style.setProperty(
        '--page-background-color',
        currentLessonRecipe.backgroundColor ?? 'var(--color-skyblue)'
    );

    // -------------------------------------------------------------------------
    // Render content by PageState
    // -------------------------------------------------------------------------
    let content;
    switch (PageState) {

        case 'LessonLobby':
            content = (
                <LessonLobby
                    pathName={lessonName}
                    teaserUrl={currentLessonRecipe.teaserUrl}
                    teaserThumbnail={'/' + currentLessonRecipe.imgPath}
                    instructor={currentLessonRecipe.instructor}
                    AnimalType={currentLessonRecipe.AnimalType}
                    instructorName={t(currentLessonRecipe.instructorName)}
                    name={t(currentLessonRecipe.name)}
                    description={t(currentLessonRecipe.description)}
                    lessonImgURL={currentLessonRecipe.imgPath}
                    stepRequirements={getLessonRequirementsArr(stepsRecipe)}
                    onContinue={handleCloseLobby}
                    VolumeSwitchState={VolumeSwitchState}
                    addEvent={addEvent}
                    onXbutton={currentLessonHasStarted || lobbyOpenedFromPanel ? handleCloseLobby : null}
                    LessonStarted={currentLessonHasStarted || lobbyOpenedFromPanel}
                    onIntroCompleted={() => {
                        const introItemId = stepsRecipe[0]?.stepItems?.[0]?.id;
                        if (introItemId) engine.onMarkItemComplete(introItemId);
                        const courseId = engine.idx.parentOf[lessonId];
                        const courseNumber = engine.idx.courseIds.indexOf(courseId) + 1;
                        try { track('appStepCompleted', { stepNumber: 1, lessonNumber: CurrentLessonIndex + 1, courseNumber }); } catch (_) {}
                    }}
                    isLocked={isLocked}
                    onSubscribe={() => setIsPWallOpen(true)}
                />
            );
            break;

        case 'LessonPanel':
            content = (
                <LessonPanel
                    FadeOutContent={false}
                    currentLessonRecipe={currentLessonRecipe}
                    stepsRecipe={stepsRecipe}
                    StepProgIndex={StepProgIndex}
                    MaxSteps={stepsRecipe.length}
                    AnimalType={currentLessonRecipe.AnimalType}
                    instructorName={t(currentLessonRecipe.instructorName)}
                    CurrentSelectedStep={CurrentSelectedStep}
                    setCurrentSelectedStep={(i) => engine.onBrowseStep(stepsRecipe[i]?.id ?? null)}
                    setCurrentSelectedStepItem={() => {}}
                    setStepProgIndex={() => {}}
                    CurrentLessonIndex={CurrentLessonIndex}
                    LessonProgIndex={LessonProgIndex}
                    handleOpenStepper={handleOpenStepper}
                    stepName={stepName}
                    handleOpenLobby={handleOpenLobby}
                    stepDescription={stepDescription}
                    StepNumberToShow={viewingStepRecipe?.StepNumberToShow ?? null}
                    coinName={viewingStepRecipe?.coinName ?? null}
                    imageToShow={viewingStepRecipe?.ImageToShow ?? null}
                    AnimalName={viewingStepRecipe?.AnimalName ?? null}
                    stepRequirements={stepRequirements}
                    StepRequirement={StepRequirement}
                    lessonStepsProgress={[]}
                    lessonHasMandatoryProgress={currentLessonHasStarted || isCursorInCurrentLesson}
                />
            );
            break;

        case 'LessonStepper':
            content = (
                <LessonStepper
                    CHEATS={CHEATS}
                    LessonType={currentLessonRecipe.type}
                    CurrentSelectedStepItem={CurrentSelectedStepItem}
                    setCurrentSelectedStepItem={() => {}}
                    IsCurrentSelectedStepItemCompleted={engine.isActiveItemCompleted}
                    stepRecipe={activeStepRecipe ?? stepsRecipe[0]}
                    stepItemId={engine.activeStepItemId}
                    handleCloseStepper={handleCloseStepper}
                    playRefAudio={playRefAudio}
                    pauseRefAudio={pauseRefAudio}
                    teamworkMusicRef={teamworkMusicRef}
                    VolumeSwitchState={VolumeSwitchState}
                    addEvent={addEvent}
                    classes={''}
                    MaxStepItems={activeStepItemIds.length}
                    AnimalType={currentLessonRecipe.AnimalType}
                    instructorName={t(currentLessonRecipe.instructorName)}
                    LessonDetails={{
                        teamworkSkillImgName: currentLessonRecipe.teamworkSkillImgName,
                        teamworkSkillName: currentLessonRecipe.teamworkSkillName,
                        teamworkSkillTodo: currentLessonRecipe.teamworkSkillTodo,
                    }}
                />
            );
            break;

        case 'EmptyPage':
            content = <div className="Lesson texutre" />;
            break;

        case 'celebrateStep':
        case 'celebreateCoin':
            content = (
                <div className="Onboarding-content celebration-container hideScrollbar">
                    <div className="Onboarding-subtitle golden-text glow">{t('general.stepComplete')}</div>
                    <StepUI
                        isCelebrate={true}
                        IsStepCelebrationStepCompleteClass={IsStepCelebrationStepCompleteClass}
                        step={stepsRecipe[celebratingStepIndex]}
                        index={celebratingStepIndex}
                        calcStepState={() => 'completed'}
                        AnimalName={stepsRecipe[celebratingStepIndex]?.AnimalName}
                        coinName={stepsRecipe[celebratingStepIndex]?.coinName}
                        ImageToShow={stepsRecipe[celebratingStepIndex]?.ImageToShow ?? null}
                        teaserThumbnail={currentLessonRecipe.teaserThumbnail}
                    />
                    <img
                        className="celebration-background"
                        src="/assets/backgrounds/celebration-bg.svg"
                        alt="celebration background"
                    />
                </div>
            );
            break;

        case 'celebrateLesson':
            content = (
                <>
                    <div className="Onboarding-content celebration-container hideScrollbar">
                        <div className="Onboarding-subtitle golden-text glow">{t('general.lessonComplete')}</div>
                        <div className="Onboarding-titleXL">{t(currentLessonRecipe.name)}</div>
                        <div className="Onboarding-subtitle">
                            {t('general.lessonCompleteDescription')} {t(currentLessonRecipe.name)}!
                        </div>
                        <div className={'Lesson_Trophy_XL ' + (IsStepCelebrationStepCompleteClass ? 'jello-vertical' : '')}>
                            <img
                                className={IsStepCelebrationStepCompleteClass ? 'bounceDelayed' : ''}
                                alt="Lesson complete!"
                                src="/assets/icons/icon_trophy-no-shadow.svg"
                            />
                            <div className="glowCircle" />
                        </div>
                    </div>
                    <img
                        className="celebration-background"
                        src="/assets/backgrounds/celebration-bg.svg"
                        alt="celebration background"
                    />
                    <div className="Onboarding-footer">
                        <button
                            onClick={tapOnLessonEnded}
                            className="button Onboarding-button buttonCatbears buttonCatbears_CTA arcadeBtn"
                        >
                            {t('general.continue')}
                        </button>
                    </div>
                </>
            );
            break;

        default:
            content = <SomethingWrong />;
            break;
    }

    const isCreateAccountStepItem =
        PageState === 'LessonStepper' &&
        activeStepRecipe?.stepItems?.[CurrentSelectedStepItem]?.pageState === 'createAccount';

    return (
        <>
            {!isCreateAccountStepItem && (
                <TopNavbar
                    showNavBG={false}
                    removeNavStrip={true}
                    showLogo={false}
                    hideLogoOnMobile={true}
                    hideLogoOnDesktop={true}
                    authContext={UserContext}
                    user={user}
                    title={false}
                    backgroundColor={false}
                    border={false}
                    setVolumeSwitchState={setVolumeSwitchState}
                    VolumeSwitchState={VolumeSwitchState}
                />
            )}
            <audio ref={teamworkMusicRef} src="https://media.catbears.com/catbears-teamwork-theme-song_silent.mp3" />
            <audio ref={happyMusicRef} src="https://media.catbears.com/catbears-happy.mp3" />
            <audio ref={stepCompleteSfxRef} src="/assets/audio/Ni Sound - Cute Interface -  Bubble Pop Synth Chord Positive Notification.wav" />
            <audio ref={lessonCompleteSfxRef} src="/assets/audio/Ni Sound - Matching Game - Positive Notification Ascending Synth.wav" />
            <>
                {CHEATS && (
                    <div className="CHEATS">
                        <div className="CHEATS-miniButton" onClick={() => setCHEATS(false)}>X</div>
                        <div className="CHEATS-flex-row">
                            <button
                                className="CHEATS-miniButton"
                                onClick={() => console.log(JSON.stringify(engine.progressById, null, 2))}
                            >🖨️</button>
                            <button
                                className="CHEATS-miniButton"
                                onClick={() => window.location.reload()}
                            >🔄</button>
                            <button
                                className="CHEATS-miniButton"
                                onClick={() => engine.devOverrideProgress({}, engine.idx.stepItemIds[0])}
                            >🗑️</button>
                            <button
                                className="CHEATS-miniButton"
                                onClick={() => engine.onBack()}
                            >👈</button>
                            <button
                                className="CHEATS-miniButton"
                                onClick={() => engine.onContinue()}
                            >👉</button>
                        </div>
                        <CourseProgressGrid
                            courses={cheatsDisplayCourses}
                            currentLessonIndex={CurrentLessonIndex}
                            currentStepIndex={CurrentSelectedStep}
                            currentStepItemIndex={CurrentSelectedStepItem}
                            courseRecipe={CourseTheatreReicpe}
                            onUpdateProgress={(nextProgress) => {
                                // nextProgress is old nested tree { courses: [...] } from generateProgressTreeFromArray
                                const migrated = migrateProgress(engine.idx, nextProgress);
                                engine.devOverrideProgress(migrated.progressById, migrated.activeStepItemId);
                            }}
                        />
                    </div>
                )}
                {content ? content : <SomethingWrong />}
                <RegisterModal
                    isOpen={showRegisterModal}
                    onClose={handleRegisterModalClose}
                    onSuccess={handleRegisterModalSuccess}
                />
            </>
        </>
    );
}

export default Lesson;
